/**
 * Placement defaults (D36): the values new components and lines start with,
 * set once so a schematic is captured at the right tube or channel size rather
 * than fixed element by element afterwards.
 *
 * Every template this kind of design can use is listed, lines first (they are
 * what is drawn most), then the palette's sections. Each parameter shows the
 * value a new element would start with and where it comes from. The same
 * table can be saved for this design, kept in the document so it versions with
 * it and is the same for everyone who edits it, or as this person's defaults,
 * kept in the browser for designs that set none. Nothing already drawn
 * changes: each element keeps the values it was created with.
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { DesignKind, TemplateDocument } from "../design/document";
import { suitsDeclaration, type PlacementDefaults } from "../editor";
import { ParameterField, ParameterTableHead } from "./Inspector";
import { connectorTemplates, paletteGroups, type WorkspaceLibrary } from "./library";
import { sameValue } from "./parameters";

interface Entry {
  entityId: string;
  versionId: string;
  name: string;
  template: TemplateDocument;
}

interface Group {
  label: string;
  entries: Entry[];
}

/** Lines, then the palette's sections: only templates with parameters to set. */
function defaultableGroups(library: WorkspaceLibrary, kind: DesignKind): Group[] {
  const entry = (versionId: string): Entry | null => {
    const template = library.scene.templates[versionId];
    const info = library.versions[versionId];
    if (!template || !info || (template.parameters ?? []).length === 0) return null;
    return { entityId: info.entityId, versionId, name: info.name, template };
  };
  const present = (entries: (Entry | null)[]) => entries.filter((candidate): candidate is Entry => candidate !== null);
  const lines = present(connectorTemplates(library, kind).map(({ versionId }) => entry(versionId)));
  const sections = paletteGroups(library, "", kind).map((group) => ({
    label: group.label,
    entries: present(group.items.map((item) => entry(item.versionId))),
  }));
  return [{ label: "Lines", entries: lines }, ...sections].filter((group) => group.entries.length > 0);
}

/** One layer's values for a template, kept only where the declaration accepts them. */
function accepted(template: TemplateDocument, values: Readonly<Record<string, unknown>> | undefined) {
  const kept: Record<string, unknown> = {};
  for (const declaration of template.parameters ?? []) {
    const value = values?.[declaration.key];
    if (value !== undefined && suitsDeclaration(declaration, value)) kept[declaration.key] = value;
  }
  return kept;
}

type Working = Record<string, Record<string, unknown>>;

/** The working table: this person's defaults with the design's over them, as placement layers them. */
function initialWorking(groups: readonly Group[], design: PlacementDefaults, user: PlacementDefaults): Working {
  const working: Working = {};
  for (const { entries } of groups) {
    for (const { entityId, template } of entries) {
      const values = { ...accepted(template, user[entityId]), ...accepted(template, design[entityId]) };
      // Only what differs from the template: the rest is the template's own.
      for (const declaration of template.parameters ?? []) {
        if (declaration.key in values && sameValue(values[declaration.key], declaration.default ?? null)) {
          delete values[declaration.key];
        }
      }
      if (Object.keys(values).length > 0) working[entityId] = values;
    }
  }
  return working;
}

/** The working table as it is saved, keeping entries for templates not listed here (another kind). */
function asDefaults(working: Working, previous: PlacementDefaults, listed: ReadonlySet<string>): PlacementDefaults {
  const kept = Object.fromEntries(Object.entries(previous).filter(([entity]) => !listed.has(entity)));
  for (const [entity, values] of Object.entries(working)) {
    if (Object.keys(values).length > 0) kept[entity] = { ...values };
  }
  return kept;
}

function sourceOf(key: string, value: unknown, design: Readonly<Record<string, unknown>>, user: Readonly<Record<string, unknown>>) {
  if (value === undefined) return "template";
  if (key in design && sameValue(design[key], value)) return "design";
  if (key in user && sameValue(user[key], value)) return "user";
  return "changed";
}

const SOURCE_TEXT = {
  template: null,
  design: "Set for this design",
  user: "Your default",
  changed: "Not saved yet",
} as const;

export function DefaultsDialog({
  library,
  kind,
  designDefaults,
  userDefaults,
  onSaveDesign,
  onSaveUser,
  onClose,
}: {
  library: WorkspaceLibrary;
  kind: DesignKind;
  designDefaults: PlacementDefaults;
  userDefaults: PlacementDefaults;
  onSaveDesign: (defaults: PlacementDefaults) => void;
  onSaveUser: (defaults: PlacementDefaults) => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const groups = useMemo(() => defaultableGroups(library, kind), [library, kind]);
  const entries = useMemo(() => groups.flatMap((group) => group.entries), [groups]);
  const listed = useMemo(() => new Set(entries.map((entry) => entry.entityId)), [entries]);
  const [working, setWorking] = useState<Working>(() => initialWorking(groups, designDefaults, userDefaults));
  const [chosen, setChosen] = useState<string | null>(entries[0]?.entityId ?? null);
  const current = entries.find((entry) => entry.entityId === chosen) ?? entries[0];

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>(".ws-defaults-list button")?.focus();
    return () => previous?.focus?.();
  }, []);

  // Escape closes; Tab stays inside, as in the other dialogs.
  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [
      ...(dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled])",
      ) ?? []),
    ];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const setValue = (entityId: string, key: string, value: unknown) =>
    setWorking((table) => {
      const values = { ...(table[entityId] ?? {}) };
      if (value === null || value === undefined) delete values[key];
      else values[key] = value;
      const { [entityId]: _previous, ...rest } = table;
      void _previous;
      return Object.keys(values).length > 0 ? { ...rest, [entityId]: values } : rest;
    });

  const resetTemplate = (entityId: string) =>
    setWorking((table) => {
      const { [entityId]: _previous, ...rest } = table;
      void _previous;
      return rest;
    });

  const count = Object.values(working).reduce((total, values) => total + Object.keys(values).length, 0);

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className="dialog card ws-defaults"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <div className="ws-defaults-head">
          <h3 id={titleId}>Placement defaults</h3>
          <p className="ws-muted ws-small">
            What new components and lines start with. Anything already drawn keeps its values.
          </p>
        </div>

        {entries.length === 0 ? (
          <p className="ws-muted">The library has no templates with parameters for this kind of design.</p>
        ) : (
          <div className="ws-defaults-body">
            <nav className="ws-defaults-list" aria-label="Templates">
              {groups.map((group) => (
                <div key={group.label} role="group" aria-label={group.label}>
                  <h4>{group.label}</h4>
                  <ul>
                    {group.entries.map((entry) => {
                      const set = Object.keys(working[entry.entityId] ?? {}).length;
                      return (
                        <li key={entry.entityId}>
                          <button
                            type="button"
                            aria-current={entry.entityId === current?.entityId ? "true" : undefined}
                            onClick={() => setChosen(entry.entityId)}
                          >
                            <span>{entry.name}</span>
                            {set > 0 && (
                              <span className="ws-defaults-count" title={`${set} set`}>
                                <span aria-hidden="true">{set}</span>
                                <span className="visually-hidden">{`, ${set} set`}</span>
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>

            {current && (
              <section className="ws-defaults-template" aria-label={current.name}>
                <div className="ws-defaults-template-head">
                  <h4>{current.name}</h4>
                  {Object.keys(working[current.entityId] ?? {}).length > 0 && (
                    <button type="button" className="ws-link" onClick={() => resetTemplate(current.entityId)}>
                      Reset to template
                    </button>
                  )}
                </div>
                {current.template.summary && <p className="ws-muted ws-small">{current.template.summary}</p>}
                <table className="ws-params ws-resizable">
                  <ParameterTableHead />
                    {(current.template.parameters ?? []).map((declaration) => {
                      const value = working[current.entityId]?.[declaration.key];
                      const defaultValue = declaration.default ?? null;
                      const effective = value ?? defaultValue;
                      const source =
                        SOURCE_TEXT[
                          sourceOf(
                            declaration.key,
                            value,
                            designDefaults[current.entityId] ?? {},
                            userDefaults[current.entityId] ?? {},
                          )
                        ];
                      return (
                        <ParameterField
                          // A different template, or a reset, starts each field afresh.
                          key={`${current.entityId}:${declaration.key}`}
                          refId={current.name}
                          row={{
                            key: declaration.key,
                            declaration,
                            effective,
                            defaultValue,
                            overridden: value !== undefined && !sameValue(value, defaultValue),
                          }}
                          locked={null}
                          serverFindings={[]}
                          note={source}
                          onCommit={(next) => setValue(current.entityId, declaration.key, next)}
                        />
                      );
                    })}
                </table>
              </section>
            )}
          </div>
        )}

        <div className="row dialog-actions ws-defaults-actions">
          <span className="ws-muted ws-small ws-defaults-summary" role="status">
            {count === 0 ? "Template defaults throughout" : `${count} value${count === 1 ? "" : "s"} differ from the templates`}
          </span>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            title="Kept in this browser, for any design that does not set its own"
            onClick={() => onSaveUser(asDefaults(working, userDefaults, listed))}
          >
            Save as my defaults
          </button>
          <button
            type="button"
            className="primary"
            title="Kept with this design, for everyone who edits it"
            onClick={() => onSaveDesign(asDefaults(working, designDefaults, listed))}
          >
            Save for this design
          </button>
        </div>
      </div>
    </div>
  );
}
